/*
 * Batch Clean panel — whole-document and selection apply.
 * Ported from handleApplyBatch / handleApplySelectionBatch in taskpane.js.
 */

import { useState } from "react";
import { MessageBar, MessageBarBody } from "@fluentui/react-components";
import { useStore } from "../../state/store";
import { useTranslation } from "../../i18n/useTranslation";
import { resolvePlanAwareError } from "../../api/planErrors";
import { UpgradePlanButton } from "../common/UpgradePlanButton";
import { resolveRuleSelection, buildSelectionSignature, buildKnownPresetSignatures } from "../../rules/ruleLogic";
import {
  getDocumentAsBase64,
  insertCorrectedDocument,
  readSelectionText,
} from "../../office/document";
import { replaceSelectionText } from "../../office/selection";
import { insertSelectionComment } from "../../office/commentFindings";
import { applyGreekEditorToDocx, applyGreekEditorToText, fetchCustomRulePresets, recordRecentRuleSet } from "../../api/client";
import type { ApplyTextResult } from "../../api/client";
import type { DocComment, DocxReviewMode } from "../../state/types";

function getCorrectedText(
  result: ApplyTextResult | null,
  originalText: string,
  invalidMessage: string
): string {
  const corrected =
    typeof result?.correctedText === "string"
      ? result.correctedText
      : typeof result?.text === "string"
        ? result.text
        : typeof result?.outputText === "string"
          ? result.outputText
          : null;

  if (typeof corrected === "string" && (corrected.length > 0 || !originalText.trim())) {
    return corrected;
  }
  throw new Error(invalidMessage);
}

export function BatchPanel(): JSX.Element {
  const { t, lang } = useTranslation();
  const rules = useStore((s) => s.rules);
  const token = useStore((s) => s.token);
  const selectedRuleIds = useStore((s) => s.selectedRuleIds);
  const preferences = useStore((s) => s.preferences);

  const [reviewMode, setReviewMode] = useState<DocxReviewMode>("tracked");
  const [percent, setPercent] = useState(0);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [busy, setBusy] = useState(false);

  const step = (pct: number, msg: string) => {
    setPercent(pct);
    setMessage(msg);
  };

  const ruleIds = resolveRuleSelection(rules, selectedRuleIds);

  const applyToDocument = async () => {
    if (ruleIds.length === 0) {
      setIsError(true);
      setMessage(t.noRulesSelected);
      return;
    }
    setBusy(true);
    setIsError(false);
    setShowUpgrade(false);
    try {
      step(10, t.statusReading);
      const base64Doc = await getDocumentAsBase64();
      step(50, t.statusProcessing);
      const corrected = await applyGreekEditorToDocx(token, base64Doc, ruleIds, reviewMode, {
        ...preferences,
      });
      step(85, t.statusWriting);
      await insertCorrectedDocument(corrected);
      step(100, t.statusDone);
      try {
        const customPresets = await fetchCustomRulePresets();
        const known = buildKnownPresetSignatures(rules, lang, customPresets, preferences);
        const signature = buildSelectionSignature(selectedRuleIds, preferences);
        if (!known.has(signature)) {
          await recordRecentRuleSet({
            ruleIds: selectedRuleIds,
            preferences,
          });
        }
      } catch {
        // history is best-effort; never fail the run because recording failed
      }
    } catch (err) {
      const error = resolvePlanAwareError(err, {
        quota: t.quotaExceededMessage,
        forbidden: t.serviceForbiddenMessage,
        fallback: t.statusError,
      });
      setIsError(true);
      setShowUpgrade(error.showUpgrade);
      setMessage(`${t.statusError} ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const applyToSelection = async () => {
    if (ruleIds.length === 0) {
      setIsError(true);
      setMessage(t.noRulesSelected);
      return;
    }
    setBusy(true);
    setIsError(false);
    setShowUpgrade(false);
    try {
      step(15, t.statusReadingSelection);
      const selectedText = await readSelectionText();
      if (!selectedText?.trim()) throw new Error(t.emptySelection);

      step(50, t.statusProcessingSelection);
      const result = await applyGreekEditorToText(token, selectedText, ruleIds, {
        ...preferences,
      });

      const totalReplacements = (result?.summary as { totalReplacements?: number } | undefined)
        ?.totalReplacements;
      const totalComments = (result?.summary as { totalComments?: number } | undefined)
        ?.totalComments;
      if (totalReplacements === 0 && totalComments === 0) {
        step(
          100,
          lang === "el" ? "Δεν βρέθηκαν σφάλματα για διόρθωση." : "No issues were found to fix."
        );
        return;
      }

      const correctedText = getCorrectedText(result, selectedText, t.invalidApplyTextResponse);
      const changes = result?.report?.changes || result?.changes;
      const comments: DocComment[] = result?.report?.comments || result?.comments || [];

      /*
       * Comment-only rules do not rewrite text, so selection apply must avoid
       * treating zero replacements as zero findings and add Word comments itself.
       */
      step(80, t.statusWritingSelection);
      if ((totalReplacements || 0) > 0) {
        await replaceSelectionText(
          correctedText,
          reviewMode,
          changes,
          selectedText,
          t.trackedSelectionUnsupported
        );
      }
      for (const comment of comments) {
        await insertSelectionComment(comment, selectedText, t.commentUnsupported);
      }
      step(100, t.selectionApplied);
      try {
        const customPresets = await fetchCustomRulePresets();
        const known = buildKnownPresetSignatures(rules, lang, customPresets, preferences);
        const signature = buildSelectionSignature(selectedRuleIds, preferences);
        if (!known.has(signature)) {
          await recordRecentRuleSet({
            ruleIds: selectedRuleIds,
            preferences,
          });
        }
      } catch {
        // history is best-effort; never fail the run because recording failed
      }
    } catch (err) {
      const error = resolvePlanAwareError(err, {
        quota: t.quotaExceededMessage,
        forbidden: t.serviceForbiddenMessage,
        fallback: t.statusError,
      });
      setIsError(true);
      setShowUpgrade(error.showUpgrade);
      setMessage(`${t.statusError} ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const selectedCount = selectedRuleIds.length;
  const ruleSummary =
    selectedCount > 0
      ? t.selectedRulesCount.replace("{count}", String(selectedCount))
      : t.noRulesSelected;

  return (
    <div className="panel-section">
      <div className="panel-card info-card">{t.batchInfo}</div>

      <fieldset className="segmented-control" aria-label={t.reviewModeLabel} disabled={busy}>
        <button
          type="button"
          className={`segmented-control__option${
            reviewMode === "tracked" ? " segmented-control__option--active" : ""
          }`}
          onClick={() => setReviewMode("tracked")}
          aria-pressed={reviewMode === "tracked"}
          disabled={busy}
        >
          {t.reviewModeTracked}
        </button>
        <button
          type="button"
          className={`segmented-control__option${
            reviewMode === "direct" ? " segmented-control__option--active" : ""
          }`}
          onClick={() => setReviewMode("direct")}
          aria-pressed={reviewMode === "direct"}
          disabled={busy}
        >
          {t.reviewModeClean}
        </button>
      </fieldset>

      <div className="panel-card rule-summary-card">
        <span className="rule-summary-card__text">{t.rulesDependencyInfo}</span>
        <span className="rule-summary-card__count">{ruleSummary}</span>
      </div>

      <div className="actions-stack">
        <button
          type="button"
          className="control-button control-button--primary"
          onClick={applyToDocument}
          disabled={busy || ruleIds.length === 0}
        >
          {t.applyBtn}
        </button>
        <button
          type="button"
          className="control-button control-button--secondary"
          onClick={applyToSelection}
          disabled={busy || ruleIds.length === 0}
        >
          {t.applySelectionBtn}
        </button>
      </div>

      {percent > 0 ? (
        <div className="progress-line" aria-label={message || t.statusProcessing}>
          <div className="progress-line__bar" style={{ width: `${percent}%` }} />
        </div>
      ) : null}
      {message ? (
        isError ? (
          <MessageBar intent="error" className="status-card">
            <MessageBarBody>
              {message}
              {showUpgrade ? <UpgradePlanButton label={t.quotaExceededUpgrade} /> : null}
            </MessageBarBody>
          </MessageBar>
        ) : (
          <div className="muted-text">{message}</div>
        )
      ) : (
        <div className="muted-text">{t.applyReadyStatus}</div>
      )}
    </div>
  );
}
